/**
 * CreateClientIdeaModal
 *
 * Client portal: capture-first form for a client to propose an idea.
 * Title is required; description and one reference link are optional.
 * The parent owns the API call and closes the modal on success, so this
 * component is a pure controlled form. Fields reset whenever it closes.
 */

import React, { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { SpinnerIcon } from "@/components/icons";
import type { CreateClientIdeaInput } from "../services/clientIdeasService";
import { buildCreateClientIdeaInput, canSubmitIdea } from "./createClientIdeaForm";

interface CreateClientIdeaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (input: CreateClientIdeaInput) => void;
  isSubmitting: boolean;
  error?: string | null;
}

export const CreateClientIdeaModal: React.FC<CreateClientIdeaModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  error,
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [link, setLink] = useState("");

  // Modal stays mounted while closed, so reset the draft on close to avoid
  // showing stale input the next time it opens.
  useEffect(() => {
    if (!isOpen) {
      setTitle("");
      setDescription("");
      setLink("");
    }
  }, [isOpen]);

  const canSubmit = canSubmitIdea(title, isSubmitting);

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit(buildCreateClientIdeaInput(title, description, link));
  };

  const footer = (
    <>
      <button
        type="button"
        onClick={onClose}
        disabled={isSubmitting}
        className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 disabled:opacity-50"
      >
        Annulla
      </button>
      <button
        type="button"
        onClick={handleSubmit}
        disabled={!canSubmit}
        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
      >
        {isSubmitting && <SpinnerIcon className="w-4 h-4" />}
        Invia
      </button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Proponi un'idea"
      size="md"
      bodyPadding
      footer={footer}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        className="space-y-4"
      >
        <div>
          <label
            htmlFor="idea-title"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Titolo
          </label>
          <input
            id="idea-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Es. Dietro le quinte in cucina"
            autoFocus
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label
            htmlFor="idea-description"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Descrizione <span className="text-gray-400">(facoltativa)</span>
          </label>
          <textarea
            id="idea-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Racconta la tua idea…"
            rows={4}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none"
          />
        </div>

        <div>
          <label
            htmlFor="idea-link"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Link di riferimento{" "}
            <span className="text-gray-400">(facoltativo)</span>
          </label>
          <input
            id="idea-link"
            type="url"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="https://…"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {error && (
          <div className="bg-rose-50 border border-rose-200 rounded-md px-3 py-2 text-sm text-rose-700">
            {error}
          </div>
        )}

        {/* Submit lives in the modal footer; this hidden button lets Enter
            submit the form from any field. */}
        <button type="submit" className="hidden" aria-hidden="true" />
      </form>
    </Modal>
  );
};
